import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { Navigation } from '@quizzach/components';
import { Reveal } from '@quizzach/motion';
import {
	FaqQRow,
	FaqRows,
	InstructionsHead,
	InstructionsPage,
} from '@quizzach/styles';
import { scrollToTop } from '@quizzach/utils';

type Props = {
	question: string;
	answer: string;
	hand?: string;
	isExpanded: boolean;
	onToggle: () => void;
};

const InstructionsItem = ({
	question,
	answer,
	hand,
	isExpanded,
	onToggle,
}: Props) => (
	<FaqQRow className={isExpanded ? 'open' : ''} onClick={onToggle}>
		<div className='q'>
			<h3>{question}</h3>
			<span className='tgl' aria-hidden='true' />
		</div>

		<AnimatePresence initial={false}>
			{isExpanded && (
				<motion.div
					onClick={(e) => e.stopPropagation()}
					key='answer'
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: 'auto', opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={{ duration: 0.3, ease: 'easeInOut' }}
					style={{ overflow: 'hidden', cursor: 'auto' }}
				>
					<div className='a'>
						<p>{answer}</p>
						{hand && <span className='hand'>{hand}</span>}
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	</FaqQRow>
);

type ItemData = {
	question: string;
	answer: string;
	hand?: string;
};

type ChapterData = {
	label: string;
	items: ItemData[];
};

/* Game-flow answers first, then the six purchase/rules answers shared
   with /faq (referenced, not copied), grouped into themed chapters. */
const chapters: ChapterData[] = [
	{
		label: 'instructions.ch1',
		items: [
			{ question: 'instructions.q1', answer: 'instructions.a1' },
			{ question: 'instructions.q2', answer: 'instructions.a2' },
			{ question: 'instructions.q3', answer: 'instructions.a3' },
		],
	},
	{
		label: 'instructions.ch2',
		items: [
			{
				question: 'instructions.q4',
				answer: 'instructions.a4',
			},
			{
				question: 'instructions.q5',
				answer: 'instructions.a5',
			},
		],
	},
	{
		label: 'instructions.ch3',
		items: [
			{ question: 'footer.faq.q1', answer: 'footer.faq.a1' },
			{ question: 'footer.faq.q2', answer: 'footer.faq.a2' },
			{ question: 'footer.faq.q3', answer: 'footer.faq.a3' },
		],
	},
	{
		label: 'instructions.ch4',
		items: [
			{ question: 'footer.faq.q4', answer: 'footer.faq.a4' },
			{ question: 'footer.faq.q5', answer: 'footer.faq.a5' },
			{ question: 'footer.faq.q6', answer: 'footer.faq.a6' },
		],
	},
];

export const Instructions = () => {
	const { t } = useTranslation();
	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

	useEffect(() => {
		scrollToTop();
	}, []);

	return (
		<InstructionsPage>
			<Navigation />

			<InstructionsHead>
				<Reveal as='span' className='hand'>
					{t('instructions.kick')}
				</Reveal>
				<Reveal as='h1'>
					{t('instructions.title1')} <em>{t('instructions.titleAccent')}</em>
				</Reveal>
			</InstructionsHead>

			<Reveal>
				<FaqRows data-id='instructions-container'>
					{chapters.map((chapter, chapterIndex) => (
						<div className='chapter' key={chapter.label}>
							<span className='ch'>{t(chapter.label)}</span>
							{chapter.items.map((item, itemIndex) => {
								const index = chapterIndex * 100 + itemIndex;

								return (
									<InstructionsItem
										key={item.question}
										question={t(item.question)}
										answer={t(item.answer)}
										hand={item.hand ? t(item.hand) : undefined}
										isExpanded={expandedIndex === index}
										onToggle={() =>
											setExpandedIndex(expandedIndex === index ? null : index)
										}
									/>
								);
							})}
						</div>
					))}
				</FaqRows>
			</Reveal>
		</InstructionsPage>
	);
};
